/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

///<reference types="slickgrid"/>
///<reference types="slickgrid/slick.checkboxselectcolumn"/>
///<reference types="slickgrid/slick.rowselectionmodel"/>

/**
 * @author Yann Massard (yamass@gmail.com)
 */

import * as $ from "jquery";
import {TeamAppsEvent} from "./util/TeamAppsEvent";
import {UiMultiRecordFieldContainer_FieldEventEvent, UiMultiRecordFieldContainer_FieldValueChangedEvent} from "../generated/UiMultiRecordFieldContainerConfig";
import {
	UiTable_CellEditingStartedEvent,
	UiTable_CellEditingStoppedEvent,
	UiTable_ColumnSizeChangeEvent,
	UiTable_FieldOrderChangeEvent,
	UiTable_FooterFieldEventEvent,
	UiTable_FooterFieldValueChangedEvent,
	UiTable_HeaderFieldEventEvent,
	UiTable_HeaderFieldValueChangedEvent,
	UiTable_MultipleRowsSelectedEvent,
	UiTable_RequestNestedDataEvent,
	UiTable_RowSelectedEvent,
	UiTable_SortingChangedEvent,
	UiTable_TableDataRequestEvent,
	UiTableCommandHandler,
	UiTableConfig,
	UiTableEventSource
} from "../generated/UiTableConfig";
import {UiField, ValueChangeEventData} from "./formfield/UiField";
import {UiFieldConfig} from "../generated/UiFieldConfig";
import {UiComponentConfig} from "../generated/UiComponentConfig";
import {DEFAULT_TEMPLATES, keyCodes} from "trivial-components";
import {UiDropDownButtonConfig} from "../generated/UiDropDownButtonConfig";
import {UiTableColumnConfig} from "../generated/UiTableColumnConfig";
import {UiCompositeField} from "./formfield/UiCompositeField";
import {UiCompositeFieldConfig} from "../generated/UiCompositeFieldConfig";
import {UiItemView} from "./UiItemView";
import {UiCompositeFieldTableCellEditor} from "./table-editors/UiCompositeFieldTableCellEditor";
import {debouncedMethod} from "./util/debounce";
import {UiComponent} from "./UiComponent";
import {UiDropDown} from "./micro-components/UiDropDown";
import {TeamAppsUiContext} from "./TeamAppsUiContext";
import {executeWhenAttached} from "./util/ExecuteWhenAttached";
import {manipulateWithoutTransitions} from "./Common";
import {UiSortDirection} from "../generated/UiSortDirection";
import {EventFactory} from "../generated/EventFactory";
import {TeamAppsUiComponentRegistry} from "./TeamAppsUiComponentRegistry";
import {SubEventRegistrator} from "../generated/SubEventRegistrator";
import {UiFieldEditingMode} from "../generated/UiFieldEditingMode";
import {UiSubEvent} from "../generated/UiSubEvent";
import {SubEventFactory} from "../generated/SubEventFactory";
import {UiItemViewConfig} from "../generated/UiItemViewConfig";
import {SubCommandExecutor} from "../generated/SubCommandExecutor";
import EventData = Slick.EventData;
import OnHeaderRowCellRenderedEventArgs = Slick.OnHeaderRowCellRenderedEventArgs;
import OnBeforeHeaderRowCellDestroyEventArgs = Slick.OnBeforeHeaderRowCellDestroyEventArgs;
import {UiGenericTableCellEditor} from "./table-editors/UiGenericTableCellEditor";
import {FixedSizeTableCellEditor} from "./table-editors/FixedSizeTableCellEditor";
import {createUiColorCssString} from "./util/CssFormatUtil";

export interface TableDataProviderItem {
	[fieldName: string]: any;

	__children: TableDataProviderItem[];
	__itemMetaData: {
		cssClasses: string;     // TODO lazy init
		fieldCssClasses: { [fieldName: string]: string }; // TODO lazy init
		depth: number;
	}
}

class TableDataProvider {
	public onDataLoading = new Slick.Event();

	private static LOOKAHAED = 50;
	private timerId: number = null;

	private data: TableDataProviderItem[];
	private numberOfRootNodes: number;

	constructor(
		data: any[],
		private dataRequestCallback: Function,
		private idPropertyName: string,
		private parentIdPropertyName: string,
		private expandedPropertyName: string
	) {
		this.data = this.prepareData(data);
		this.numberOfRootNodes = this.data.filter(item => !item[this.parentIdPropertyName]).length;
	}

	private prepareData(flatData: any[], parentId: number | string = 0, depth = 0): TableDataProviderItem[] {
		let itemsById: { [index: number]: any } = {};
		for (let i = 0; i < flatData.length; i++) {
			let item = flatData[i];
			itemsById[item[this.idPropertyName]] = item;
			item.__itemMetaData = item.__itemMetaData = {
				cssClasses: "",  // TODO lazy init
				fieldCssClasses: {}  // TODO lazy init
			};
		}

		for (let i = 0; i < flatData.length; i++) {
			let item = flatData[i];
			if (item[this.parentIdPropertyName]) {
				let parentItem = itemsById[item[this.parentIdPropertyName]];
				if (parentItem) {
					parentItem.__children || (parentItem.__children = []);
					parentItem.__children.push(item);
				}
			}
		}

		let resultData = flatData.filter(item => item[this.parentIdPropertyName] == parentId || (!item[this.parentIdPropertyName] && !parentId));

		function setDepth(node: TableDataProviderItem, depth: number) {
			node.__itemMetaData.depth = depth;
			if (node.__children && node.__children.length > 0) {
				node.__children.forEach(child => setDepth(child, depth + 1));
			}
		}

		for (let i = 0; i < resultData.length; i++) {
			let rootNode = resultData[i];
			setDepth(rootNode, depth);
		}

		for (let i = resultData.length - 1; i >= 0; i--) { // iterate backwards since we are manipulating the array we are iterating on...
			const tableDataProviderItem = resultData[i];

			if (this.isRowExpanded(tableDataProviderItem) && tableDataProviderItem.__children && tableDataProviderItem.__children.length > 0) {
				let visibleDescendants = this.getListOfVisibleDescendants(resultData[i]);
				resultData.splice.apply(resultData, ([i + 1, 0] as any[]).concat(visibleDescendants));
			}
		}

		return resultData;
	}

	private isRowExpanded(tableDataProviderItem: any): boolean {
		return !!tableDataProviderItem[this.expandedPropertyName];
	}

	public toggleRowExpanded(rowIndex: number) {
		let item = this.data[rowIndex];
		if (!item) {
			return;
		}
		if (!this.isRowExpanded(item)) {
			item[this.expandedPropertyName] = true;
			let visibleDescendants = this.getListOfVisibleDescendants(this.data[rowIndex]);
			this.data.splice.apply(this.data, ([rowIndex + 1, 0] as any).concat(visibleDescendants));
		} else {
			item[this.expandedPropertyName] = false;
			this.data.splice(rowIndex + 1, this.findEndOfChildrenIndex(rowIndex) - (rowIndex + 1));
		}
	}

	private getListOfVisibleDescendants(item: TableDataProviderItem): TableDataProviderItem[] {
		let visibleDescendants: TableDataProviderItem[] = [];

		let addNodesAndExpandedDescendants = (nodes: any[]) => {
			for (let child of nodes) {
				visibleDescendants.push(child);
				if (child.__children && this.isRowExpanded(child)) {
					addNodesAndExpandedDescendants(child.__children);
				}
			}
		};

		item.__children && addNodesAndExpandedDescendants(item.__children);
		return visibleDescendants;
	}

	/**
	 * @param parentItemRowIndex
	 * @returns {number} the index of the first non-child row following the node specified by parentItemRowIndex
	 */
	private findEndOfChildrenIndex(parentItemRowIndex: number) {
		let parentNode = this.data[parentItemRowIndex];
		if (!parentNode) { // seems not to be initialized yet, so no children
			return parentItemRowIndex + 1;
		}
		let parentIds = [parentNode[this.idPropertyName]];
		for (var followingRowIndex = parentItemRowIndex + 1; followingRowIndex < this.data.length; followingRowIndex++) {
			let row = this.data[followingRowIndex];
			if (!row) {
				break;
			}
			let rowParentId = row[this.parentIdPropertyName];
			if (rowParentId !== 0 && rowParentId === parentIds[parentIds.length - 1]) {
				// same level as before
			} else if (rowParentId === this.data[followingRowIndex - 1][this.idPropertyName]) {
				// nested row
				parentIds.push(rowParentId);
			} else {
				// non-sibling and non-nested => higher level.
				while (parentIds.length > 0 && parentIds[parentIds.length - 1] != rowParentId) {
					parentIds.pop();
				}
				if (parentIds.length === 0) {
					break;
				}
			}
		}
		return followingRowIndex;
	};

	/**
	 * This method is called by SlickGrid.
	 */
	public getLength(): number {
		return this.data.length;
	}

	public setTotalNumberOfRootNodes(totalNumberOfRootNodes: number) {
		let numberOfVisibleNonRootChildren = this.data.filter(node => node && node.__itemMetaData.depth > 0).length;
		this.numberOfRootNodes = totalNumberOfRootNodes;
		this.data.length = totalNumberOfRootNodes + numberOfVisibleNonRootChildren;
		// TODO #events Fire event because data.length has changed!
	}

	public getTotalNumberOfRootNodes() {
		return this.numberOfRootNodes;
	}

	/**
	 * This method is called by SlickGrid.
	 */
	public getItem(index: number): TableDataProviderItem {
		return this.data[index];  // yep, that's ok!
	}

	/**
	 * This method is called by SlickGrid.
	 */
	public getItemMetadata(index: number): any {
		if (!this.data[index]) {
			return null;
		} else {
			return this.data[index].__itemMetaData;
		}
	}

	public addRowStyleClass(recordId: number | string, cssClass: string): number {
		let rowIndex = this.findVisibleRowIndexById(recordId);
		let node: TableDataProviderItem;
		if (rowIndex != null) {
			node = this.data[rowIndex];
		} else { // node is hidden or does not exist
			node = this.getNodeById(recordId);
		}
		node && (node.__itemMetaData.cssClasses += " " + cssClass);
		return rowIndex; // TODO #events Fire event instead of returning rowIndex!!
	}

	public removeRowStyleClass(recordId: number | string, cssClass: string): number {
		let rowIndex = this.findVisibleRowIndexById(recordId);
		let node: TableDataProviderItem;
		if (rowIndex != null) {
			node = this.data[rowIndex];
		} else { // node is hidden or does not exist
			node = this.getNodeById(recordId);
		}
		let itemMetadata = node.__itemMetaData;
		itemMetadata.cssClasses = itemMetadata.cssClasses.replace(new RegExp(cssClass, "g"), "");
		return rowIndex; // TODO #events Fire event instead of returning rowIndex!!
	}

	public clear(): void {
		this.data = [];
	}

	public updateRootNodeData(startRootNodeIndex: number, newData: any[]) {
		let rowIndex = 0;
		let rootNodeIndex = 0;
		while (rootNodeIndex < startRootNodeIndex) {
			rowIndex = this.findEndOfChildrenIndex(rowIndex);
			rootNodeIndex++;
		}

		let insertRowIndex = rowIndex;
		let numberOfRootNodesInNewData = newData.filter(item => !item._parentId).length;
		let newPreparedData = this.prepareData(newData);

		for (let i = 0; i < numberOfRootNodesInNewData; i++) {
			rowIndex = this.findEndOfChildrenIndex(rowIndex);
		}

		this.data.splice.apply(this.data, (<any[]>[insertRowIndex, rowIndex - insertRowIndex]).concat(newPreparedData));
	}

	setChildrenData(parentRecordId: number | string, tableData: any[]) {
		let parent = this.getNodeById(parentRecordId);

		if (parent) {
			let preparedData = this.prepareData(tableData, parentRecordId, parent.__itemMetaData.depth + 1).filter(item => item[this.parentIdPropertyName] === parentRecordId); // throw everything away that is not below the parent node!
			let parentItemRowIndex = this.findVisibleRowIndexById(parentRecordId);
			if (parentItemRowIndex != null) {
				let endOfCurrentChildrenIndex = this.findEndOfChildrenIndex(parentItemRowIndex);
				this.data.splice.apply(this.data, (<any[]>[parentItemRowIndex + 1, endOfCurrentChildrenIndex - parentItemRowIndex - 1]).concat(preparedData));
			}
			parent.__children = preparedData;
		}
	}

	public removeItem(recordId: string | number): boolean {
		let rowIndex = this.findVisibleRowIndexById(recordId);
		let node: TableDataProviderItem;
		if (rowIndex !== null) {
			node = this.data[rowIndex];
		} else { // the node is a hidden child or does not exist...
			node = this.getNodeById(recordId);
		}

		if (!node) {
			return;
		}

		if (rowIndex != null) {
			this.data.splice.call(this.data, rowIndex, this.findEndOfChildrenIndex(rowIndex) - rowIndex);
		}

		if (node[this.parentIdPropertyName]) {
			let parentNode = this.getNodeById(node[this.parentIdPropertyName]);
			parentNode.__children = parentNode.__children.filter(node => node[this.idPropertyName] != recordId);
		}
	}


	public ensureData(firstVisibleRowIndex: number, lastVisibleRowIndex: number) {
		if (firstVisibleRowIndex > lastVisibleRowIndex) {
			return;
		}

		let from = Math.max(firstVisibleRowIndex - TableDataProvider.LOOKAHAED, 0);
		let to = lastVisibleRowIndex + TableDataProvider.LOOKAHAED;
		if (this.data.length) {
			to = Math.min(to, this.data.length - 1);
		}

		while (this.data[from] !== undefined && from < to) {
			from++;
		}
		while (this.data[to] !== undefined && from < to) {
			to--;
		}

		if (firstVisibleRowIndex > to || lastVisibleRowIndex < from) { // not really necessary to load anything
			return;
		}

		if (from == to && this.data[to] !== undefined) {
			return;
		}

		if (this.timerId != null) {
			clearTimeout(this.timerId);
		}

		this.timerId = window.setTimeout(() => {
			for (let i = from; i <= to; i++) {
				this.data[i] = null; // null indicates a 'requested but not available yet'
			}

			let length = to - from + 1;
			this.dataRequestCallback(from, length);
		}, 100);
	}

	public findVisibleRowIndexById(id: number | string): number {
		if (id === null || id === undefined) {
			return null;
		}
		for (let i = 0; i < this.data.length; i++) {
			const entry = this.data[i];
			if (entry != null && entry[this.idPropertyName] === id) {
				return i;
			}
		}
		return null;
	}

	public getNodeById(id: number | string): TableDataProviderItem {
		let rowIndex = this.findVisibleRowIndexById(id);
		if (rowIndex != null) {
			return this.data[rowIndex];
		} else {
			return this.findNodes(item => item && item[this.idPropertyName] === id)[0];
		}
	}

	private findNodes(predicate: (item: TableDataProviderItem) => boolean): TableDataProviderItem[] {
		let rootNodes = this.data.filter(node => node && !node[this.parentIdPropertyName]);

		let foundNodes: TableDataProviderItem[] = [];

		function find(node: TableDataProviderItem) {
			if (predicate.call(null, node)) {
				foundNodes.push(node);
			}
			if (node.__children && node.__children.length > 0) {
				node.__children.forEach(child => find(child));
			}
		}

		for (let i = 0; i < rootNodes.length; i++) {
			let rootNode = rootNodes[i];
			find(rootNode);
		}

		return foundNodes;
	}

	public setFieldCssClass(recordId: number | string, fieldName: string, className: string, enable: boolean) {
		let node = this.getNodeById(recordId);
		let metaData = node && node.__itemMetaData;
		if (!metaData) {
			return;
		}
		if (metaData.fieldCssClasses[fieldName] == null) {
			metaData.fieldCssClasses[fieldName] = "";
		}
		// TODO #performance: change data structure to something simpler than strings!
		metaData.fieldCssClasses[fieldName] = metaData.fieldCssClasses[fieldName].replace(new RegExp("\\b" + className + "\\b"), "").replace(" {2,}", " ");
		if (enable) {
			metaData.fieldCssClasses[fieldName] = " " + className;
		}
	}

	public clearAllFieldCssClasses(className: string) {
		let allNodes = this.findNodes(node => true);
		for (let rowIndex = 0; rowIndex < allNodes.length; rowIndex++) {
			let itemMetadata = allNodes[rowIndex].__itemMetaData;
			if (itemMetadata) {
				for (let fieldName in itemMetadata.fieldCssClasses) {
					itemMetadata.fieldCssClasses[fieldName] = itemMetadata.fieldCssClasses[fieldName].replace(new RegExp("\\b" + className + "\\b"), "").replace(" {2,}", " ");
				}
			}
		}
	}

	public pushNewLastRow(row: any) {
		this.data.push(...this.prepareData([row]));
		this.numberOfRootNodes++;
	}
}

export class UiTable extends UiComponent<UiTableConfig> implements UiTableCommandHandler, UiTableEventSource {

	public readonly onCellEditingStarted: TeamAppsEvent<UiTable_CellEditingStartedEvent> = new TeamAppsEvent<UiTable_CellEditingStartedEvent>(this);
	public readonly onCellEditingStopped: TeamAppsEvent<UiTable_CellEditingStoppedEvent> = new TeamAppsEvent<UiTable_CellEditingStoppedEvent>(this);
	public readonly onFieldValueChanged: TeamAppsEvent<UiMultiRecordFieldContainer_FieldValueChangedEvent> = new TeamAppsEvent<UiMultiRecordFieldContainer_FieldValueChangedEvent>(this);
	public readonly onFieldEvent: TeamAppsEvent<UiMultiRecordFieldContainer_FieldEventEvent> = new TeamAppsEvent<UiMultiRecordFieldContainer_FieldEventEvent>(this);
	public readonly onRowSelected: TeamAppsEvent<UiTable_RowSelectedEvent> = new TeamAppsEvent<UiTable_RowSelectedEvent>(this);
	public readonly onMultipleRowsSelected: TeamAppsEvent<UiTable_MultipleRowsSelectedEvent> = new TeamAppsEvent<UiTable_MultipleRowsSelectedEvent>(this);
	public readonly onSortingChanged: TeamAppsEvent<UiTable_SortingChangedEvent> = new TeamAppsEvent<UiTable_SortingChangedEvent>(this);
	public readonly onTableDataRequest: TeamAppsEvent<UiTable_TableDataRequestEvent> = new TeamAppsEvent<UiTable_TableDataRequestEvent>(this);
	public readonly onRequestNestedData: TeamAppsEvent<UiTable_RequestNestedDataEvent> = new TeamAppsEvent<UiTable_RequestNestedDataEvent>(this);
	public readonly onFieldOrderChange: TeamAppsEvent<UiTable_FieldOrderChangeEvent> = new TeamAppsEvent<UiTable_FieldOrderChangeEvent>(this);
	public readonly onColumnSizeChange: TeamAppsEvent<UiTable_ColumnSizeChangeEvent> = new TeamAppsEvent<UiTable_ColumnSizeChangeEvent>(this);
	public readonly onHeaderFieldValueChanged: TeamAppsEvent<UiTable_HeaderFieldValueChangedEvent> = new TeamAppsEvent<UiTable_HeaderFieldValueChangedEvent>(this);
	public readonly onHeaderFieldEvent: TeamAppsEvent<UiTable_HeaderFieldEventEvent> = new TeamAppsEvent<UiTable_HeaderFieldEventEvent>(this);
	public readonly onFooterFieldValueChanged: TeamAppsEvent<UiTable_FooterFieldValueChangedEvent> = new TeamAppsEvent<UiTable_FooterFieldValueChangedEvent>(this);
	public readonly onFooterFieldEvent: TeamAppsEvent<UiTable_FooterFieldEventEvent> = new TeamAppsEvent<UiTable_FooterFieldEventEvent>(this);

	private $component: JQuery;
	private _grid: Slick.Grid<any>;
	private allColumns: any[];
	private columnVisibilities: { [fieldName: string]: boolean };
	private dataProvider: TableDataProvider;
	private _$loadingIndicator: JQuery;
	private loadingIndicatorFadeInTimer: number;
	private newRecordIdCounter = -1;

	private _sortField: string;
	private _sortDirection: UiSortDirection;

	private lastSelectedRowIds: number[];

	private doNotFireEventBecauseSelectionIsCausedByApiCall: boolean = false; // slickgrid fires events even if we set the selection via api...

	private dropDown: UiDropDown;
	private $selectionFrame: JQuery;
	private headerRowFields: { [fieldName: string]: UiField } = {};
	private footerRowFields: { [fieldName: string]: UiField } = {};

	private editorFields: { [fieldName: string]: UiField } = {};
	private $editorFieldTempContainer: JQuery;

	constructor(config: UiTableConfig, context: TeamAppsUiContext) {
		super(config, context);
		this.$component = $(`<div class="UiTable" id="${config.id}">
    <style>
        #${config.id} .slick-cell {
          border-bottom-width: ${config.rowBorderWidth}px;
          border-bottom-color: ${createUiColorCssString(config.rowBorderColor)};
        }
        #${config.id} .slick-cell.selected {
          background-color: ${createUiColorCssString(config.selectionColor)};
        }
        #${config.id} .striped-rows .slick-row.even {
          background-color: ${createUiColorCssString(config.stripedRowColorEven)};
        }
        #${config.id} .striped-rows .slick-row.odd {
          background-color: ${createUiColorCssString(config.stripedRowColorOdd)};
        }
        #${config.id} .slick-headerrow {
          background-color: ${createUiColorCssString(config.headerRowBackgroundColor)};
          border-bottom: ${config.headerRowBorderWidth}px solid ${createUiColorCssString(config.headerRowBorderColor)};
        }
        #${config.id} .slick-footerrow {
          background-color: ${createUiColorCssString(config.footerRowBackgroundColor)};
          border-top: ${config.footerRowBorderWidth}px solid ${createUiColorCssString(config.footerRowBorderColor)};
        }
    </style>
    <div class="slick-table"></div>
    <div class="editor-field-temp-container"></div>
</div>`);
		const $table = this.$component.find(".slick-table");
		if (config.stripedRows) {
			$table.addClass("striped-rows");
		}
		this.$editorFieldTempContainer = this.$component.find(".editor-field-temp-container");

		this.createSlickGrid(config, $table);
		this.dropDown = new UiDropDown();
		this.dropDown.onComponentRemoved = (component: UiComponent<UiComponentConfig>) => component.destroy();
		this.dropDown.onClose = (component?: UiComponent<UiComponentConfig>) => component && component.destroy();
	}

	private isRowExpanded(item: any): boolean {
		return item[this._config.expandedPropertyName];
	}

	@executeWhenAttached()
	private createSlickGrid(config: UiTableConfig, $table: JQuery) {
		this.allColumns = this._createColumns();

		if (config.iconPropertyName) {
			const IconFormatter: Slick.Formatter<TableDataProviderItem> = (row: number, cell: number, value: any, columnDef: Slick.Column<TableDataProviderItem>, dataContext: TableDataProviderItem) => {
				let iconName = dataContext[config.iconPropertyName];
				const backgroundImageStyle = iconName ? `style="background-image: url('${this._context.getIconPath(iconName, 16)}')"` : '';
				return `<div class="img img-16" ${backgroundImageStyle}></div>`;
			};
			this.allColumns.unshift({
				id: "__iconColumn",
				name: "",
				field: "",
				formatter: IconFormatter,
				cssClass: "justify-center",
				width: 30,
				maxWidth: 30,
				cannotTriggerInsert: true,
				resizable: false,
				unselectable: true,
				sortable: false,
				focusable: false
			});
		}
		if (config.showRowCheckBoxes) {
			var checkboxSelector = new Slick.CheckboxSelectColumn({
				cssClass: "justify-center"
			});
			this.allColumns.unshift(checkboxSelector.getColumnDefinition());
		}
		if (config.showRecordIdNumbering) {
			const IdNumberFormatter: Slick.Formatter<TableDataProviderItem> = (row: number, cell: number, value: any, columnDef: Slick.Column<TableDataProviderItem>, dataContext: TableDataProviderItem) => {
				return dataContext[config.idProperty];
			};
			this.allColumns.unshift({
				id: "__idColumn",
				name: "ID",
				field: "",
				formatter: IdNumberFormatter,
				cssClass: "justify-right",
				headerCssClass: "justify-right",
				width: 45,
				cannotTriggerInsert: true,
				resizable: true,
				unselectable: true,
				sortable: false,
				focusable: false
			});
		}
		if (config.showNumbering) {
			const RowNumberFormatter: Slick.Formatter<TableDataProviderItem> = (row: number, cell: number, value: any, columnDef: Slick.Column<TableDataProviderItem>, dataContext: TableDataProviderItem) => {
				return "" + (row + 1);
			};
			this.allColumns.unshift({
				id: "__rowNumber",
				name: "#",
				field: "",
				formatter: RowNumberFormatter,
				cssClass: "justify-right",
				headerCssClass: "text-right",
				width: 35,
				maxWidth: 75,
				cannotTriggerInsert: true,
				resizable: true,
				unselectable: true,
				sortable: false,
				focusable: false
			});
		}

		this.columnVisibilities = this.allColumns.reduce((visibilities, column) => {
			visibilities[column.field] = !column.fieldConfig || column.fieldConfig.visible;
			return visibilities;
		}, {});

		const options: Slick.GridOptions<any> & { createFooterRow: boolean, showFooterRow: boolean, footerRowHeight: number } = {
			explicitInitialization: true,
			enableCellNavigation: true,
			enableColumnReorder: false,
			forceFitColumns: config.forceFitWidth,
			fullWidthRows: true,
			rowHeight: config.rowHeight + config.rowBorderWidth,
			multiColumnSort: false,
			multiSelect: config.allowMultiRowSelection,
			enableTextSelectionOnCells: false, // see also CSS style user-select: none
			editable: config.editable,
			editCommandHandler: (item: TableDataProviderItem, column: Slick.Column<TableDataProviderItem>, editCommand: any) => {
				let uiField = this.editorFields[(column as any).fieldConfig.fieldName];
				if (uiField.valuesChanged(editCommand.serializedValue, editCommand.prevSerializedValue)) {
					this.onFieldValueChanged.fire(EventFactory.createUiMultiRecordFieldContainer_FieldValueChangedEvent(this._config.id, item[this._config.idProperty], column.id, editCommand.serializedValue));
				}
				editCommand.execute();
			},
			showHeaderRow: config.showHeaderRow,
			headerRowHeight: config.headerRowHeight,
			createFooterRow: true,
			showFooterRow: config.showFooterRow,
			footerRowHeight: config.footerRowHeight,
		};

		this.dataProvider = new TableDataProvider(config.tableData, (fromIndex: number, length: number) => {
			this.onTableDataRequest.fire(EventFactory.createUiTable_TableDataRequestEvent(config.id, fromIndex, length, this._sortField, this._sortDirection));
		}, config.idProperty, config.parentIdProperty, config.expandedPropertyName);

		if (config.totalNumberOfRecords > this.dataProvider.getLength()) {
			this.dataProvider.setTotalNumberOfRootNodes(config.totalNumberOfRecords);
		}

		this._grid = new Slick.Grid($table, this.dataProvider, this.getVisibleColumns(), options);

		if (config.headerRowFields) {
			this.configureOuterFields(true);
		}
		if (config.footerRowFields) {
			this.configureOuterFields(false);
		}

		if (config.hideHeaders) {
			$table.find(".slick-header-columns").css({
				height: 0,
				border: "none"
			});
			//this._grid.resizeCanvas();
		}

		$table.addClass(config.displayAsList ? 'list-mode' : 'table-mode');

		this._grid.setSelectionModel(new Slick.RowSelectionModel());

		if (config.showRowCheckBoxes) {
			this._grid.registerPlugin(checkboxSelector);
		}

		this._grid.onViewportChanged.subscribe((e, args) => {
			const vp = this._grid.getViewport();
			this.dataProvider.ensureData(vp.top, vp.bottom);
		});
		this._$loadingIndicator = $(DEFAULT_TEMPLATES.defaultSpinnerTemplate).hide().appendTo($table);
		this.dataProvider.onDataLoading.subscribe(() => {
			clearTimeout(this.loadingIndicatorFadeInTimer);
			this.loadingIndicatorFadeInTimer = window.setTimeout(() => {
				this._$loadingIndicator.fadeIn();
			}, 2000);
		});
		if (config.sortField) {
			this._sortField = config.sortField;
			this._sortDirection = config.sortDirection;
			this._grid.setSortColumn(config.sortField, config.sortDirection === UiSortDirection.ASC);
		}
		this._grid.onSort.subscribe((e, args: Slick.OnSortEventArgs<Slick.SlickData>) => {
			this._sortField = (<any>args.sortCol).id;
			this._sortDirection = args.sortAsc ? UiSortDirection.ASC : UiSortDirection.DESC;
			this.onSortingChanged.fire(EventFactory.createUiTable_SortingChangedEvent(config.id, this._sortField, args.sortAsc ? UiSortDirection.ASC : UiSortDirection.DESC));
		});
		this._grid.onSelectedRowsChanged.subscribe((eventData, args) => {
			if (args.rows.some((row) => !this.dataProvider.getItem(row))) {
				return; // one of the selected rows has no id (so it does not seem to be loaded yet). ==> do not fire an event.
			}
			if (!this.doNotFireEventBecauseSelectionIsCausedByApiCall
				&& JSON.stringify(this.lastSelectedRowIds) !== JSON.stringify(args.rows)) {
				this.lastSelectedRowIds = args.rows;
				if (args.rows.length === 1) {
					this.onRowSelected.fire(EventFactory.createUiTable_RowSelectedEvent(
						config.id,
						this.dataProvider.getItem(args.rows[0])[this._config.idProperty],
						false, // see click handlers on canvas element...
						false // see click handlers on canvas element...
					));
				} else if (args.rows.length > 1) {
					this.onMultipleRowsSelected.fire(EventFactory.createUiTable_MultipleRowsSelectedEvent(
						config.id,
						args.rows.map((selectionIndex) => {
							return this.dataProvider.getItem(selectionIndex)[this._config.idProperty];
						})
					));
				}
			}
			this.updateSelectionFramePosition(true);
		});
		this._grid.onCellChange.subscribe((eventData: EventData, data) => {
			this.updateSelectionFramePosition(true);
		});
		this._grid.onClick.subscribe((e: MouseEvent, args: Slick.OnClickEventArgs<TableDataProviderItem>) => {
			setTimeout(/* make sure the table updated its activeCell property! */ () => {
				let fieldName = this._grid.getColumns()[args.cell].id;
				let uiField = config.columns.map(c => c.field).filter((field) => field.fieldName === fieldName)[0];
				let item = this.dataProvider.getItem(args.row);
				if (item) { // may be undefined if this is a new row!
					let isIndentedColumn = fieldName === this._config.indentedColumnName;
					if (isIndentedColumn && $(e.target).hasClass("teamapps-table-row-expander")) {
						$(e.target).toggleClass("expanded");
						if (item[this._config.lazyChildrenFlagProperty] && !item.__children && !this.isRowExpanded(item)) {
							this.requestLazyChildren(item[config.idProperty]);
						}
						this.dataProvider.toggleRowExpanded(args.row);
						this._grid.invalidateAllRows();
						this._grid.updateRowCount();
						this._grid.render();
					}
					let cellValue: any = item[fieldName];
					let $buttonElement = $(e.target).closest(".UiButton");
					if (uiField && ["UiButton", "UiDropDownButton"].indexOf(uiField._type) !== -1 && $buttonElement.length > 0) {
						this.onFieldValueChanged.fire(EventFactory.createUiMultiRecordFieldContainer_FieldValueChangedEvent(this._config.id, this.getActiveCellRecordId(), fieldName, true));
						if (uiField._type === "UiDropDownButton") {
							let uiDropDownButtonConfig = uiField as UiDropDownButtonConfig;
							this.dropDown.setContent(null);
							this.dropDown.open($buttonElement, {
								width: uiDropDownButtonConfig.dropDownWidth
							});
						}
					} else if (uiField && (uiField._type === "UiFileField")) {
						let $templateWrapper = $((<any>e).target).closest(".custom-entry-template-wrapper");
						if ($templateWrapper.length > 0) {
							let index = $templateWrapper.parent().index($templateWrapper);
							this.fileFieldItemClicked(uiField.fieldName, cellValue[index])
						}
					}
				}
			});
		});
		this._grid.onBeforeEditCell.subscribe((e, data: Slick.OnBeforeEditCellEventArgs<any>) => {
			let id = data.item && data.item[this._config.idProperty];
			let fieldName = this._grid.getColumns()[data.cell].field;
			if (id != null && fieldName != null) {
				this.onCellEditingStarted.fire(EventFactory.createUiTable_CellEditingStartedEvent(this.getId(), id, fieldName, data.item[fieldName]));
			}
		});
		this._grid.onBeforeCellEditorDestroy.subscribe((e, data: Slick.OnBeforeCellEditorDestroyEventArgs<any>) => {
			let id = this._grid.getDataItem(this._grid.getActiveCell().row)[this._config.idProperty];
			let fieldName = this._grid.getColumns()[this._grid.getActiveCell().cell].field;
			if (id != null && fieldName != null) {
				this.onCellEditingStopped.fire(EventFactory.createUiTable_CellEditingStoppedEvent(this.getId(), id, fieldName));
			}
		});
		$(this._grid.getCanvasNode()).dblclick((e) => {
			let cell = this._grid.getCellFromEvent(<any>e);
			if (cell != null) {
				this.onRowSelected.fire(EventFactory.createUiTable_RowSelectedEvent(
					config.id,
					this.dataProvider.getItem(cell.row)[this._config.idProperty],
					false,
					true
				));
			}
		});

		$(this._grid.getCanvasNode()).on("contextmenu", (e) => {
			let cell = this._grid.getCellFromEvent(<any>e);
			this.onRowSelected.fire(EventFactory.createUiTable_RowSelectedEvent(
				config.id,
				this.dataProvider.getItem(cell.row)[this._config.idProperty],
				true,
				false
			));
		});

		if (config.selectionFrame) {
			this.$selectionFrame = $(`<div class="UiTable-selection-frame">`)
				.css({
					"border": `${this._config.selectionFrame.borderWidth}px solid ${createUiColorCssString(this._config.selectionFrame.color)}`,
					"box-shadow": `0 0 ${this._config.selectionFrame.shadowWidth}px 0 rgba(0, 0, 0, .5), 0 0 ${this._config.selectionFrame.glowingWidth}px 0 ${createUiColorCssString(this._config.selectionFrame.color)}`,
					"transition": `top ${this._config.selectionFrame.animationDuration}ms, left ${this._config.selectionFrame.animationDuration}ms, right ${this._config.selectionFrame.animationDuration}ms, width ${this._config.selectionFrame.animationDuration}ms, height ${this._config.selectionFrame.animationDuration}ms`
				})
				.appendTo(this.$component);
		}
		this._grid.onScroll.subscribe((eventData) => {
			this.updateSelectionFramePosition();
		});
		this._grid.onColumnsResized.subscribe((eventData) => {
			this._grid.invalidateAllRows(); // needed. There are fields (currenlty only UiCompositeField) whose rendering is depending on the width.
			this._grid.render();
			this.updateSelectionFramePosition();
		});
		this._grid.init();

		if (this._config.ensureEmptyLastRow && this._config.totalNumberOfRecords === this.dataProvider.getTotalNumberOfRootNodes()) {
			this.addEmptyLastRow();
		}

		if (this._config.headerRowData) {
			this.setOuterRowData(true, this._config.headerRowData);
		}
		if (this._config.footerRowData) {
			this.setOuterRowData(false, this._config.footerRowData);
		}
	}

	private configureOuterFields(header: boolean) {
		let outerRowFieldsMember = header ? this.headerRowFields : this.footerRowFields;

		let renderedEvent: Slick.Event<OnHeaderRowCellRenderedEventArgs<any>> = header ? this._grid.onHeaderRowCellRendered : (this._grid as any).onFooterRowCellRendered;
		renderedEvent.subscribe((e, args) => {
			$(args.node).empty();
			let outerRowFieldConfigs = header ? this._config.headerRowFields : this._config.footerRowFields;
			let fieldConfig = outerRowFieldConfigs.filter(f => f.fieldName === args.column.id)[0];
			if (fieldConfig) {
				let uiField = <UiField> new (TeamAppsUiComponentRegistry.getFieldClassForName(fieldConfig._type))(fieldConfig, $(args.node), this._context);
				outerRowFieldsMember[fieldConfig.fieldName] = uiField;
				uiField.onValueChanged.addListener(valueChangedEventData => {
					if (header) {
						this.onHeaderFieldValueChanged.fire(EventFactory.createUiTable_HeaderFieldValueChangedEvent(this._config.id, fieldConfig.fieldName, valueChangedEventData.value));
					} else {
						this.onFooterFieldValueChanged.fire(EventFactory.createUiTable_FooterFieldValueChangedEvent(this._config.id, fieldConfig.fieldName, valueChangedEventData.value));
					}
				});
				SubEventRegistrator.registerForUiFieldSubEvents(uiField, (eventObject, emitter) => {
					if (header) {
						this.onHeaderFieldEvent.fire(EventFactory.createUiTable_HeaderFieldEventEvent(this.getId(), fieldConfig.fieldName, eventObject));
					} else {
						this.onFooterFieldEvent.fire(EventFactory.createUiTable_FooterFieldEventEvent(this.getId(), fieldConfig.fieldName, eventObject));
					}
				});
			}
		});
		let beforeDestroyEvent: Slick.Event<OnBeforeHeaderRowCellDestroyEventArgs<any>> = header ? this._grid.onBeforeHeaderRowCellDestroy : (this._grid as any).onBeforeFooterRowCellDestroy;
		beforeDestroyEvent.subscribe((e, args) => {
			outerRowFieldsMember[args.column.id].destroy();
		});
	}

	public getMainDomElement(): JQuery {
		return this.$component;
	}

	protected onAttachedToDom() {
		//this._grid.updateCanvasWidth(true);
		this.reLayout();
	}

	private _createColumns(): any[] {
		const columns = [];
		for (let i = 0; i < this._config.columns.length; i++) {
			const columnConfig: UiTableColumnConfig = this._config.columns[i];
			const fieldConfig: UiFieldConfig = columnConfig.field;

			let uiField = this.createEditorField(fieldConfig);
			this.editorFields[fieldConfig.fieldName] = uiField;

			let editorFactory;
			if (fieldConfig._type === "UiCompositeField") {
				editorFactory = UiCompositeFieldTableCellEditor.bind(null, uiField, () => uiField.getMainDomElement().appendTo(this.$editorFieldTempContainer));
			} else if (fieldConfig._type === "UiRichTextEditor" || fieldConfig._type === "UiMultiLineTextField") {
				editorFactory = FixedSizeTableCellEditor.bind(null, uiField, () => uiField.getMainDomElement().appendTo(this.$editorFieldTempContainer));
			} else {
				editorFactory = UiGenericTableCellEditor.bind(null, uiField, () => uiField.getMainDomElement().appendTo(this.$editorFieldTempContainer));
			}

			let columnCssClass;
			if (["UiNumberField", "UiCurrencyField"].indexOf(fieldConfig._type) != -1) {
				columnCssClass = 'justify-right';
			} else if (['UiCheckBox', 'UiButton', 'UiDropDownButton'].indexOf(fieldConfig._type) != -1) {
				columnCssClass = 'justify-center';
			} else {
				columnCssClass = '';
			}
			columns.push({
				id: fieldConfig.fieldName,
				field: fieldConfig.fieldName,
				fieldConfig: fieldConfig,
				name: columnConfig.title,
				width: columnConfig.defaultWidth || ((columnConfig.minWidth + columnConfig.maxWidth) / 2) || undefined,
				minWidth: 30 || undefined,
				maxWidth: columnConfig.maxWidth || undefined,
				formatter: this.createCellFormatter(fieldConfig),
				editor: editorFactory,
				asyncEditorLoading: false,
				autoEdit: true,
				// focusable: false makes the row NOT getting selected when clicked on this column!!
				focusable: this._config.displayAsList || (fieldConfig.editingMode !== UiFieldEditingMode.READONLY && fieldConfig.editingMode !== UiFieldEditingMode.DISABLED),
				sortable: columnConfig.sortable,
				resizable: columnConfig.resizeable,
				headerCssClass: columnCssClass,
				cssClass: columnCssClass
			});
		}
		return columns;
	}

	private createEditorField(fieldConfig: UiFieldConfig) {
		let uiField = <UiField> new (TeamAppsUiComponentRegistry.getFieldClassForName(fieldConfig._type))(fieldConfig, this.$editorFieldTempContainer, this._context);
		if (uiField.getFocusableElement()) {
			uiField.getFocusableElement().on("keydown.nav", (e) => {
				if (e.keyCode === keyCodes.left_arrow
					|| e.keyCode === keyCodes.right_arrow
					|| e.keyCode === keyCodes.up_arrow
					|| e.keyCode === keyCodes.down_arrow) {
					e.stopPropagation();
				}
			});
			uiField.getFocusableElement()
				.focus(() => uiField.getMainDomElement().css("z-index", 1000))
				.blur(() => uiField.getMainDomElement().css("z-index", 0));
		}
		SubEventRegistrator.registerForUiFieldSubEvents(uiField, (eventObject: UiSubEvent) => this.fireFieldSubEvent(fieldConfig.fieldName, eventObject));
		uiField.onValueChanged.addListener((eventData: ValueChangeEventData) => this.handleFieldValueChanged(fieldConfig.fieldName, eventData.value));
		uiField.onVisibilityChanged.addListener(visible => {
			if (this.columnVisibilities[fieldConfig.fieldName] !== undefined) {
				this.columnVisibilities[fieldConfig.fieldName] = visible;
				this._grid.setColumns(this.getVisibleColumns());
			}
		});

		if (fieldConfig._type === "UiCompositeField") {
			(uiField as UiCompositeField).onSubFieldValueChanged.addListener((eventData) => {
				this.handleFieldValueChanged(eventData.fieldName, eventData.value);
				this._grid.getDataItem(this._grid.getActiveCell().row)[eventData.fieldName] = eventData.value;
				this.onFieldValueChanged.fire(EventFactory.createUiMultiRecordFieldContainer_FieldValueChangedEvent(this._config.id, this.getActiveCellRecordId(), eventData.fieldName, eventData.value));
			});
			(uiField as UiCompositeField).onFieldSubEvent.addListener((subEventInfo) => this.fireFieldSubEvent(subEventInfo.fieldName, subEventInfo.subEvent));
		}

		return uiField;
	}

	private createCellFormatter(field: UiFieldConfig) {
		const createInnerCellFormatter = () => {
			if (field._type === "UiCompositeField") {
				return (row: number, cell: number, value: any, columnDef: Slick.Column<TableDataProviderItem>, dataContext: TableDataProviderItem) => {
					return UiCompositeField.getReadOnlyHtml(field as UiCompositeFieldConfig, {
						_type: "UiRecordValue",
						value: dataContext
					}, this._context, columnDef.width + 1);
				};
			} else if ((TeamAppsUiComponentRegistry.getFieldClassForName(field._type) as any).getReadOnlyHtml) {
				return (row: number, cell: number, value: any, columnDef: Slick.Column<TableDataProviderItem>, dataContext: TableDataProviderItem) => {
					return (TeamAppsUiComponentRegistry.getFieldClassForName(field._type) as any).getReadOnlyHtml(field, value, this._context, columnDef.width);
				};
			} else {
				return null;
			}
		};

		const innerCellFormatter = createInnerCellFormatter(); // may be undefined!
		return (row: number, cell: number, value: any, columnDef: Slick.Column<TableDataProviderItem>, dataContext: TableDataProviderItem) => {
			const innerHtml = innerCellFormatter ? innerCellFormatter(row, cell, value, columnDef, dataContext) : "###";
			const cellClass = dataContext.__itemMetaData && (dataContext.__itemMetaData.fieldCssClasses[columnDef.id] || '');
			let isIndentedColumn = this._config.indentedColumnName === columnDef.id;
			let hasChildren = dataContext.__children || dataContext[this._config.lazyChildrenFlagProperty];
			return `<div class="validation-class-wrapper ${cellClass}" style="${isIndentedColumn && dataContext.__itemMetaData.depth > 0 ? 'padding-left: ' + (dataContext.__itemMetaData.depth * this._config.indentation) + 'px' : ''}">
    ${isIndentedColumn ? `<div class="teamapps-expander teamapps-table-row-expander ${this.isRowExpanded(dataContext) ? 'expanded' : ''} ${hasChildren ? '' : 'invisible'}" data-depth="${dataContext.__itemMetaData.depth}"></div>` : ''}
    ${innerHtml}
</div>`;
		};
	}

	@executeWhenAttached()
	public clearTable() {
		this.dataProvider.clear();
		this._grid.setData(this.dataProvider, true);
		this._grid.render();
		this._grid.setSelectedRows([]);
	}

	@executeWhenAttached()
	public removeRow(recordId: any) {
		let deletedItemRowNumber = this.dataProvider.findVisibleRowIndexById(recordId);
		if (deletedItemRowNumber != null) {
			let newSelectedRows = this._grid.getSelectedRows()
				.filter(selectedRowIndex => selectedRowIndex != deletedItemRowNumber)
				.map(selectedRowIndex => selectedRowIndex > deletedItemRowNumber ? selectedRowIndex - 1 : selectedRowIndex);
			this._grid.setSelectedRows(newSelectedRows);
			this.dataProvider.removeItem(recordId);
			this._grid.invalidateAllRows();
			this._grid.render();
		}
	}

	@executeWhenAttached()
	public setTableData(startIndex: number,
	                    data: any[],
	                    totalNumberOfRecords: number,
	                    sortField: string,
	                    sortDirection: UiSortDirection,
	                    clearTableCache: boolean) {
		if (clearTableCache) {
			this.dataProvider.clear();
			this._grid.setSelectedRows([]);
			this._grid.updateRowCount();
		}

		this._sortField = sortField;
		this._sortDirection = sortDirection;
		if (sortField) {
			this._grid.setSortColumn(sortField, sortDirection === UiSortDirection.ASC);
		}

		const tableData = data;
		this.dataProvider.updateRootNodeData(startIndex, tableData);
		for (let i = 0; i < tableData.length; i++) {
			this._grid.invalidateRow(startIndex + i);
		}

		if (totalNumberOfRecords != this.dataProvider.getLength()) {
			this.dataProvider.setTotalNumberOfRootNodes(totalNumberOfRecords);
		}

		if (this._config.ensureEmptyLastRow && this.dataProvider.getTotalNumberOfRootNodes() === totalNumberOfRecords) {
			this.addEmptyLastRow();
		}

		this._grid.updateRowCount();
		this._grid.render();

		clearTimeout(this.loadingIndicatorFadeInTimer);
		this._$loadingIndicator.fadeOut();

		this._grid.resizeCanvas();
		this.updateSelectionFramePosition();
	}

	@executeWhenAttached()
	public setChildrenData(recordId: any, data: any[]) {
		let tableData = data;
		this.dataProvider.setChildrenData(recordId, tableData);

		// TODO #events
		this._grid.updateRowCount();
		this._grid.invalidateAllRows();
		this._grid.render();
	}

	@executeWhenAttached()
	public setFieldValue(recordId: any, fieldName: string, data: any) {
		const node = this.dataProvider.getNodeById(recordId);
		if (node) {
			node[fieldName] = data;
		}

		let currentCellEditor = (<any>this._grid.getCellEditor());
		let isEditorOnCurrentRecord = currentCellEditor != null && this.getActiveCellRecordId() && this.getActiveCellRecordId() === recordId;
		let isEditorOnCurrentField = isEditorOnCurrentRecord && this.getActiveCellFieldName() === fieldName;

		// update the editor if affected by the change
		if (isEditorOnCurrentRecord && currentCellEditor.uiField instanceof UiCompositeField) {
			currentCellEditor.uiField.setFieldValue(fieldName, data);
		} else if (isEditorOnCurrentField) {
			currentCellEditor.uiField.setCommittedValue(data);
		}

		// update the rest of the row.
		if (isEditorOnCurrentRecord) {
			let columns = this._grid.getColumns();
			for (let i = 0; i < columns.length; i++) {
				if (i !== this._grid.getActiveCell().cell) { // the editor has already been updated above.
					this._grid.updateCell(this._grid.getActiveCell().row, i); // Caution: This would NOT work for all editors, since it leads to a focus reset => no-go with composite field
				}
			}
		} else {
			// If the editor is not in the active row: no need to pay attention to the editor => faster way of updating the whole row.
			const rowIndex = this.dataProvider.findVisibleRowIndexById(recordId); // TODO #events ...
			if (rowIndex != null) {
				this._grid.invalidateRow(rowIndex);
				this._grid.render();
			}
		}
	}

	@executeWhenAttached()
	public setFieldVisible(fieldName: string, visible: boolean) {
		let uiField = this.editorFields[fieldName];
		if (uiField == null) {
			this.logger.error("setFieldVisible(): Could not find editor field with name: " + fieldName);
			return;
		}
		uiField.setVisible(visible);
	}

	private getVisibleColumns() {
		return this.allColumns.filter((column) => this.columnVisibilities[column.field] == true);
	}

	@executeWhenAttached()
	public markTableField(recordId: any, fieldName: string, mark: boolean) {
		this.dataProvider.setFieldCssClass(recordId, fieldName, "marked", mark);

		const rowIndex = this.dataProvider.findVisibleRowIndexById(recordId); // TODO #events
		if (rowIndex != null) {
			let editing = this._grid.getCellEditor();

			this._grid.invalidateRow(rowIndex);
			this._grid.render();

			if (editing && this.getActiveCellRecordId() && this.getActiveCellRecordId() === recordId) {
				this._grid.editActiveCell(null);
			}
		}
	}

	@executeWhenAttached()
	public clearAllFieldMarkings() {
		this.dataProvider.clearAllFieldCssClasses("marked");
		for (let i = 0; i < this.dataProvider.getLength(); i++) {
			this._grid.invalidateRow(i);
		}
		this._grid.render();
	}

	@executeWhenAttached()
	public setRecordBold(recordId: any, bold: boolean) {
		let rowIndex;
		if (bold) {
			rowIndex = this.dataProvider.addRowStyleClass(recordId, "text-bold")
		} else {
			rowIndex = this.dataProvider.removeRowStyleClass(recordId, "text-bold")
		}
		if (rowIndex != null) {
			this._grid.invalidateRow(rowIndex);
			this._grid.render();
		}
	}

	@executeWhenAttached()
	public selectSingleRow(recordId: any) {
		const rowIndex = this.dataProvider.findVisibleRowIndexById(recordId);

		this.doNotFireEventBecauseSelectionIsCausedByApiCall = true;
		try {
			if (rowIndex !== null) {
				this._grid.setSelectedRows([rowIndex]);
				this._grid.scrollRowToTop(rowIndex);
			} else {
				this._grid.setSelectedRows([]);
			}
		} finally {
			this.doNotFireEventBecauseSelectionIsCausedByApiCall = false;
		}
	}

	@executeWhenAttached()
	public focusField(recordId: any, fieldName: string) {
		const rowIndex = this.dataProvider.findVisibleRowIndexById(recordId);
		if (rowIndex != null) {
			let columnIndex = this._grid.getColumnIndex(fieldName);
			if (columnIndex != null) {
				this._grid.setActiveCell(rowIndex, columnIndex);
				this._grid.editActiveCell(null);
			} else {
				let compositeFieldColumnIndex = this.getCompositeFieldColumnForSubFieldName(fieldName);
				if (compositeFieldColumnIndex != null) {
					this._grid.setActiveCell(rowIndex, compositeFieldColumnIndex);
					this._grid.editActiveCell(null);
					((<any> this._grid.getCellEditor()).uiField as UiCompositeField).focusField(fieldName);
				}
			}
		}
	}

	@executeWhenAttached(true)
	@debouncedMethod(300)
	public onResize(): void {
		this._grid.resizeCanvas();
		this._grid.invalidateAllRows(); // read-only cell renderers might depend on the column width...
		this._grid.render();
		this._grid.getCellEditor() && (this._grid.getCellEditor() as any).onResize();
		this.updateSelectionFramePosition();
	}

	private handleFieldValueChanged(fieldName: string, value: any): void {
		if (this._config.ensureEmptyLastRow && this._grid.getActiveCell().row === this._grid.getDataLength() - 1) {
			this.addEmptyLastRow(true);
		}
		// TODO check if this has updated a boolean field and update composite fields accordingly (field visibilities)
	}

	private fireFieldSubEvent(fieldName: string, eventObject: UiSubEvent) {
		this.onFieldEvent.fire(EventFactory.createUiMultiRecordFieldContainer_FieldEventEvent(this._config.id, this.getActiveCellRecordId(), fieldName, eventObject));
	}

	private getCompositeFieldColumnForSubFieldName(fieldName: string): number {
		if (this.allColumns.filter(c => c.id == fieldName).length !== 0) {
			return null; // this is a normal field...
		}
		for (let col = 0; col < this.allColumns.length; col++) {
			let fieldConfig = this.allColumns[col].fieldConfig;
			if (fieldConfig && fieldConfig._type === "UiCompositeField") {
				let compositeFieldConfig = fieldConfig as UiCompositeFieldConfig;
				if (compositeFieldConfig.subFields.filter(subField => subField.field.fieldName === fieldName).length > 0) {
					return col;
				}
			}
		}
		return null;
	}

	fileFieldItemClicked(fieldName: string, buttonData: any): void {
		this.onFieldEvent.fire(EventFactory.createUiMultiRecordFieldContainer_FieldEventEvent(this._config.id, this.getActiveCellRecordId(), fieldName, SubEventFactory.createUiFileField_FileClickedSubEvent(buttonData)));
	}

	private requestLazyChildren(recordId: any) {
		this.onRequestNestedData.fire(EventFactory.createUiTable_RequestNestedDataEvent(this.getId(), recordId));
	}

	private getActiveCellValue() {
		if (this._grid.getActiveCell()) {
			return this._grid.getDataItem(this._grid.getActiveCell().row)[this._grid.getColumns()[this._grid.getActiveCell().cell].field];
		}
	};

	private getActiveCellRecordId(): any {
		if (this._grid.getActiveCell()) {
			let dataItem: TableDataProviderItem = this._grid.getDataItem(this._grid.getActiveCell().row);
			return dataItem ? dataItem[this._config.idProperty] : null;
		}
	}

	private getActiveCellFieldName() {
		if (this._grid.getActiveCell()) {
			return this._grid.getColumns()[this._grid.getActiveCell().cell].id;
		}
	}

	showToolButtonDropDown(recordId: any, toolButtonId: string, dropDownItemView: UiItemViewConfig): void {
		this.logger.error("TODO implement showToolButtonDropDown()"); // TODO implement showToolButtonDropDown()
	}

	@executeWhenAttached()
	invokeOnField(fieldName: string, fieldCommand: any): void {
		let uiField = this.editorFields[fieldName];
		if (uiField == null) {
			this.logger.error("invokeOnField(): Could not find editor field with name: " + fieldName);
			return;
		}
		SubCommandExecutor.executeCommand(uiField, fieldCommand);
	}

	@executeWhenAttached()
	invokeOnFieldIfActive(recordId: any, fieldName: string, fieldCommand: any): void {
		let activeCellRecordId = this.getActiveCellRecordId();
		let isCurrentRecord = activeCellRecordId && activeCellRecordId === recordId;
		let isCurrentField = this.getActiveCellFieldName() === fieldName;
		let currentCellEditor = (<any>this._grid.getCellEditor());
		if (currentCellEditor != null && isCurrentRecord) { // if not, too late, the user switched the cell
			if (isCurrentField) {
				SubCommandExecutor.executeCommand(currentCellEditor.uiField, fieldCommand);
			} else if (currentCellEditor.uiField instanceof UiCompositeField) {
				(currentCellEditor.uiField as UiCompositeField).invokeOnField(fieldName, fieldCommand);
			}
		}
	}

	@executeWhenAttached()
	invokeOnHeaderField(fieldName: string, fieldCommand: any): void {
		let field = this.headerRowFields[fieldName];
		if (field) {
			if (fieldCommand.type === 'UiDropDownButton.setDropDownItemView') {
				this.dropDown.setContent(new UiItemView(fieldCommand.itemView, this._context));
			} else {
				SubCommandExecutor.executeCommand(field, fieldCommand);
			}
		}
	}

	@executeWhenAttached()
	invokeOnFooterField(fieldName: string, fieldCommand: any): void {
		let field = this.footerRowFields[fieldName];
		if (field) {
			if (fieldCommand.type === 'UiDropDownButton.setDropDownItemView') {
				this.dropDown.setContent(new UiItemView(fieldCommand.itemView, this._context));
			} else {
				SubCommandExecutor.executeCommand(field, fieldCommand);
			}
		}
	}

	public destroy(): void {
		this.dropDown.destroy();
		Object.keys(this.editorFields).forEach(fieldName => this.editorFields[fieldName].destroy());
	}

	private addEmptyLastRow(scrollTo?: boolean) {
		let item = {[this._config.idProperty]: this.newRecordIdCounter--};
		this.dataProvider.pushNewLastRow(item);
		this._grid.updateRowCount();
		if (scrollTo) {
			this._grid.scrollRowIntoView(this.dataProvider.getLength() - 1, false);
		}
		this._grid.render();
	}

	private updateSelectionFramePosition(animate: boolean = false) {
		let selectionFrame = this._config.selectionFrame;
		if (selectionFrame == null) {
			return;
		}
		let activeCellNode = this._grid.getActiveCellNode();
		if (activeCellNode == null) {
			manipulateWithoutTransitions(this.$selectionFrame, () => {
				let $cell: JQuery = $(activeCellNode);
				this.$selectionFrame
					.css({
						top: -10000,
						left: -10000
					});
			}, false);
		} else {
			manipulateWithoutTransitions(this.$selectionFrame, () => {
				let $cell: JQuery = $(activeCellNode);
				this.$selectionFrame
					.css({
						top: ($cell.offset().top - this.$component.offset().top) - selectionFrame.borderWidth,
						left: selectionFrame.fullRow ? -selectionFrame.borderWidth : parseInt($cell.css('left')) - selectionFrame.borderWidth - this._grid.getViewport().leftPx,
						width: selectionFrame.fullRow ? $cell.parent().width() + 2 * selectionFrame.borderWidth + 1 : $cell.parent().width() - parseInt($cell.css('left')) - parseInt($cell.css('right')) + 2 * selectionFrame.borderWidth - 1,
						height: $cell.outerHeight() + 2 * selectionFrame.borderWidth - this._config.rowBorderWidth
					});
			}, animate);
		}
	}

	setHeaderData(data: any): void {
		this.setOuterRowData(true, data);
	}

	setHeaderFieldValue(fieldName: string, value: any): void {
		this.setOuterRowFieldValue(true, fieldName, value);
	}

	focusHeaderField(fieldName: string): void {
		this.focusOuterRowField(true, fieldName);
	}

	setFooterData(data: any): void {
		this.setOuterRowData(false, data);
	}

	setFooterFieldValue(fieldName: string, value: any): void {
		this.setOuterRowFieldValue(false, fieldName, value);
	}

	focusFooterField(fieldName: string): void {
		this.focusOuterRowField(false, fieldName);
	}

	@executeWhenAttached()
	private setOuterRowData(header: boolean, data: any) {
		let outerRowFieldsMember = header ? this.headerRowFields : this.footerRowFields;
		for (let fieldName in outerRowFieldsMember) {
			outerRowFieldsMember[fieldName].setCommittedValue(null);
		}
		if (data != null) {
			Object.keys(data).forEach(fieldName => {
				const field = outerRowFieldsMember[fieldName];
				if (field) {
					field.setCommittedValue(data[fieldName]);
				}
			});
		}

		// TODO check if this has updated a boolean field and update composite fields accordingly (field visibilities)
	}

	@executeWhenAttached()
	private setOuterRowFieldValue(header: boolean, fieldName: string, data: any) {
		let outerRowFieldsMember = header ? this.headerRowFields : this.footerRowFields;
		let field = outerRowFieldsMember[fieldName];
		if (field) {
			field.setCommittedValue(data);
		}
	}

	@executeWhenAttached()
	private focusOuterRowField(header: boolean, fieldName: string) {
		let outerRowFieldsMember = header ? this.headerRowFields : this.footerRowFields;
		let field = outerRowFieldsMember[fieldName];
		if (field) {
			field.focus();
		}
	}
}

TeamAppsUiComponentRegistry.registerComponentClass("UiTable", UiTable);